<template>
  <div class="st-input" :id="rootId">
    <label class="st-input__label" v-if="labelName" :for="id">{{
      labelName
    }}</label>
    <input
      :id="id"
      :type="computedType"
      :value="value"
      :placeholder="placeholder"
      :class="classList"
      :disabled="disabled"
      :tabindex="tabindex"
      :aria-required="required"
      :aria-invalid="invalid"
      :aria-errormessage="id + '-error'"
      :aria-autocomplete="ariaAutocomplete"
      :autocomplete="autocomplete"
      :aria-activedescendant="activedescendant"
      :aria-controls="ariaControls"
      @input="onInput"
      @change="onChange"
      @blur="onBlur"
      @focus="onFocus"
      @click="onClick"
      @keydown.down.prevent="onKeydownDown"
      @keydown.up.prevent="onKeydownUp"
      @keyup.esc="onKeyupEsc"
      @keydown.enter.prevent="onKeydownEnter"
      @keydown.delete="onKeydownDelete"
    />
    <div class="st-input__action" v-if="type === 'password'">
      <st-checkbox :id="`${id}-checkbox`" v-model="showingPassword">
        <span>Visa lösenord</span></st-checkbox
      >
    </div>
  </div>
</template>

<script>
import { version } from "vue";
import StCheckbox from "../checkbox/Checkbox.vue";
export default {
  name: "st-input",
  components: {
    StCheckbox,
  },
  data() {
    return {
      localValue: this.value,
      showingPassword: false,
    };
  },
  props: {
    id: {
      type: String,
      default: "",
      required: true,
    },
    required: {
      type: Boolean,
      default: false,
      required: true,
    },
    type: {
      type: String,
      default: "text",
      validator: function (type) {
        return ["text", "number", "date", "tel", "password"].includes(type);
      },
    },
    placeholder: {
      type: String,
    },
    value: {
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    tabindex: {
      type: String,
      default: "0",
      required: true,
    },
    invalid: {
      type: Boolean,
      default: false,
      required: true,
    },
    blur: {
      type: Function,
    },
    focus: {
      type: Function,
    },
    click: {
      type: Function,
    },
    input: {
      type: Function,
    },
    keydownDown: {
      type: Function,
    },
    keydownUp: {
      type: Function,
    },
    keyupEsc: {
      type: Function,
    },
    keydownEnter: {
      type: Function,
    },
    keydownDelete: {
      type: Function,
    },
    autocomplete: {
      type: String,
      default: "off",
    },
    isSearchable: {
      type: Boolean,
      default: false,
    },
    rootId: {
      type: String,
    },
    activedescendant: {
      type: String,
    },
    ariaControls: {
      type: String,
    },
    ariaAutocomplete: {
      type: String,
    },
    labelName: {
      type: String,
    },
  },

  computed: {
    isVue3() {
      return version && parseInt(version) === 3;
    },
    classList() {
      const input = "st-input__input-field";
      return [
        input,
        this.isSearchable && `${input}--is-searchable`,
        this.invalid && `${input}--is-invalid`,
      ];
    },
    computedType() {
      if (this.type === "password") {
        return this.showingPassword ? "text" : "password";
      } else {
        return "text";
      }
    },
  },

  watch: {
    value(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.localValue = newVal;
      }
    },
    localValue(newVal, oldVal) {
      if (newVal !== oldVal && !this.isVue3) {
        this.$emit("input", newVal);
      }
    },
  },

  methods: {
    onInput(evt) {
      this.localValue = evt.target.value;
      if (this.input) this.input();
    },
    onChange(evt) {
      this.localValue = evt.target.value;
      if (!this.isVue3) this.$emit("change", this.localValue);
    },
    onBlur() {
      if (this.blur) this.blur();
    },
    onFocus() {
      if (this.focus) this.focus();
    },
    onClick() {
      if (this.click) this.click();
    },
    onKeydownDown() {
      if (!this.isVue3) this.$emit("keydownDown");
      if (this.keydownDown) this.keydownDown();
    },
    onKeydownUp() {
      if (!this.isVue3) this.$emit("keydownUp");
      if (this.keydownUp) this.keydownUp();
    },
    onKeyupEsc(event) {
      if (this.keyupEsc) {
        event.stopPropagation();
        this.keyupEsc();
      }
    },
    onKeydownDelete() {
      if (this.keydownDelete) this.keydownDelete();
    },
    onKeydownEnter() {
      if (this.keydownEnter) this.keydownEnter();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../scss/main.scss";
.st-input {
  display: flex;
  flex-grow: 1;
  flex-direction: column;

  &__label {
    color: $dark-grey;
    font-family: $source-sans-pro;
    padding-left: $xs;
    margin-bottom: rem-calc(2);
    font-size: rem-calc(14);
    font-weight: 600;
    line-height: 1.4;
    text-align: left;
  }
  &__input-field {
    height: rem-calc(48);
    border: $border-width--thin solid $dark-grey;
    border-radius: $border-radius;
    color: $dark-grey;
    font-family: $source-sans-pro;
    width: 100%;
    box-sizing: border-box;
    padding-left: $lg;
    font-size: rem-calc(16);
    font-weight: normal;
    font-style: normal;
    font-stretch: normal;
    line-height: 1.5;
    letter-spacing: normal;
    margin-bottom: 0 !important;

    &--is-invalid {
      border-color: $danger-color-primary;
      border-width: $border-width;
    }

    &--is-searchable {
      background: url("../../icons/st/ic-search.svg") rem-calc(14) center
        no-repeat;
      padding-left: $xxl;
    }

    &:focus {
      background-color: $white;
      border-color: $dark-grey;
      outline: none;
    }

    &:disabled {
      background-color: $off-white;
    }

    &::placeholder {
      color: $dark-grey;
    }
  }
  &__action {
    // Should investigate why st-checkbox has a margin..
    & > .st-checkbox {
      margin-top: rem-calc(8);
    }
  }
}
</style>
